/**
 * Page détail garantie — `/guarantees/:code` (refonte 9.P.3.C pattern Companies R1+R2).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + breadcrumb + H1 + code/group pills
 *   - 2-col Detail `lg:grid-cols-3` (main 2/3 fiche + side panel 1/3 metadata)
 *   - CardSection moderne par section
 *   - max-w-screen-xl
 *
 * 8.D — Lecture seule. Affiche la fiche statique (catalogue `GUARANTEES`) +
 * projets de mon tenant qui incluent ce code dans `guarantee_codes[]` via
 * `GET /v1/projects?guarantee_code=:code`.
 */

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { GUARANTEES, type GuaranteeCode } from "@kollectivia/shared/constants";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useProjects } from "../../lib/queries/projects";

import { ProjectStatusBadge } from "../projects/_parts/ProjectStatusBadge";

const VALID_CODES = new Set<string>(GUARANTEES.map((g) => g.code));

export function GuaranteeDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";
  const { code } = useParams<{ code: string }>();
  const isValid = !!code && VALID_CODES.has(code);

  // D6.1 — useProjects appelé inconditionnellement (rules-of-hooks).
  const fallbackCode = (GUARANTEES[0]?.code ?? "ASSURANCE_VIE") as GuaranteeCode;
  const typedCode = (isValid ? (code as GuaranteeCode) : fallbackCode);
  const { data, isLoading, error } = useProjects(
    {
      guarantee_code: typedCode,
      limit: 50,
    },
    { enabled: isValid },
  );

  if (!isValid) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <div
          role="alert"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {t("guarantees.detail.unknown_code", { code: code ?? "" })}
        </div>
        <Link
          to="/guarantees"
          className="mt-4 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          ← {t("guarantees.detail.back_to_list")}
        </Link>
      </div>
    );
  }

  const guarantee = GUARANTEES.find((g) => g.code === code)!;

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <Link
            to="/guarantees"
            className="rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            ← {t("guarantees.detail.back_to_list")}
          </Link>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {lang === "en" ? guarantee.label_en : guarantee.label_fr}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 font-mono text-xs font-medium text-slate-700">
            {guarantee.code}
          </span>
          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
            {t(`projects.guarantees.groups.${guarantee.group}`)}
          </span>
        </div>
      </header>

      {/* 2-col Detail */}
      <div className="px-0 py-6 lg:grid lg:grid-cols-3 lg:gap-8 lg:py-8">
        {/* Main column 2/3 */}
        <div className="space-y-5 lg:col-span-2">
          <CardSection title={t("guarantees.detail.section.projects")}>
            {error && (
              <div
                role="alert"
                className="sm:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error instanceof ApiException
                  ? getApiErrorMessage(error, normalizeLang(lang)) || t("errors.generic")
                  : t("errors.generic")}
              </div>
            )}
            {isLoading && !data ? (
              <p className="sm:col-span-2 text-sm text-slate-500">{t("app.loading")}</p>
            ) : data && data.items.length === 0 ? (
              <p className="sm:col-span-2 text-sm text-slate-500">
                {t("guarantees.detail.no_projects")}
              </p>
            ) : (
              <div className="sm:col-span-2 -mx-5 -mb-4 mt-1">
                <ul className="divide-y divide-slate-100">
                  {data?.items.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/projects/${p.id}`}
                        className="flex items-center justify-between px-5 py-3 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
                      >
                        <span className="font-medium text-brand-700">{p.title}</span>
                        <ProjectStatusBadge status={p.status} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardSection>
        </div>

        {/* Side panel 1/3 — fiche identity */}
        <aside className="mt-5 space-y-5 lg:col-span-1 lg:mt-0">
          <CardSection title={t("guarantees.detail.section.identity")} sideStyle>
            <FieldStacked
              label={t("guarantees.detail.fields.label_fr")}
              value={guarantee.label_fr}
            />
            <FieldStacked
              label={t("guarantees.detail.fields.label_en")}
              value={guarantee.label_en}
            />
            <FieldStacked
              label={t("guarantees.detail.fields.code")}
              value={guarantee.code}
              mono
            />
            <FieldStacked
              label={t("guarantees.detail.fields.group")}
              value={t(`projects.guarantees.groups.${guarantee.group}`)}
            />
          </CardSection>
        </aside>
      </div>
    </div>
  );
}

function CardSection({
  title,
  children,
  sideStyle = false,
}: {
  title: string;
  children: React.ReactNode;
  sideStyle?: boolean;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-100 px-5 py-3.5">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      </header>
      <div className="px-5 py-4">
        <dl className={sideStyle ? "space-y-3" : "grid gap-x-6 gap-y-4 sm:grid-cols-2"}>
          {children}
        </dl>
      </div>
    </section>
  );
}

function FieldStacked({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) {
  const display = value !== null && value !== undefined && value !== "" ? value : null;
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd
        className={
          mono
            ? "mt-0.5 font-mono text-sm font-medium text-slate-800"
            : "mt-0.5 text-sm font-medium text-slate-800"
        }
      >
        {display ?? <span className="text-slate-400">—</span>}
      </dd>
    </div>
  );
}
